"use client";

import React, { useState, useEffect, useMemo } from "react";
import { Cpu, Bot, Layers, Code, Sparkles, ExternalLink, SlidersHorizontal, Wrench, CircleCheck as CheckCircle2, ChevronRight, Microchip } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";

// Types
interface TechnicalProject {
  id: string;
  title: string;
  domain: string;
  ageRange: string;
  technology: string;
  skills: string[];
  description: string;
  challenge?: string;
  idea?: string;
  design?: string;
  programming?: string;
  testing?: string;
  result?: string;
  image: {
    src: string;
    srcset: string;
    sizes: string;
  };
  link?: string;
}

// Fallback high-acuity technical projects data
const FALLBACK_PROJECTS: Record<"en" | "vi", TechnicalProject[]> = {
  en: [{
    id: "proj-1",
    title: "Omni-Directional Autonomous Rover Chassis",
    domain: "Robotics",
    ageRange: "12-16 Yrs",
    technology: "Mecanum Wheels, PID Control, STM32",
    skills: ["Kinematics", "C++", "Closed-Loop Control"],
    description: "Heavy-duty aluminum robotics platform featuring 4-wheel independent vector movement and precision encoder telemetry.",
    challenge: "Eliminating drift during high-speed diagonal maneuvers on arena surfaces.",
    idea: "Implement high-frequency PID control loops synced with 6-axis IMU gyro feedback.",
    design: "CNC machined 6061 aluminum frame with dual-plate vibration dampening.",
    programming: "Real-time task scheduling written in C++ with custom math libraries.",
    testing: "Bench-tested on low-friction vinyl mats to verify zero-angle rotation.",
    result: "Sub-millimeter positioning accuracy over 5-meter autonomous test runs.",
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/tech_rover_chassis.png",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/tech_rover_chassis.png-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/tech_rover_chassis.png-wvc-sizes"
    }
  }, {
    id: "proj-2",
    title: "6-DOF Pneumatic Industrial Arm Prototype",
    domain: "Engineering",
    ageRange: "14-16 Yrs",
    technology: "Electro-Pneumatics, SPI Bus, Custom PCB",
    skills: ["Circuit CAD", "Pneumatic Logic", "Relay Matrix"],
    description: "Articulated robotic arm powered by proportional solenoid valves and magnetic hall-effect angle sensors.",
    challenge: "Achieving smooth pressure modulation without sudden actuator jerk.",
    idea: "Pulse-width modulated valve driver circuit paired with optical feedback rings.",
    design: "Laser-cut acrylic and reinforced polycarbonate joints with ball-bearing pivots.",
    programming: "Embedded MicroPython script with multi-threaded sensor reading.",
    testing: "500-cycle endurance pick-and-place stress testing with 250g payloads.",
    result: "Repeatable pick resolution within ±0.5mm operating tolerance.",
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/tech_robotic_arm.webp",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/tech_robotic_arm.webp-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/tech_robotic_arm.webp-wvc-sizes"
    }
  }, {
    id: "proj-3",
    title: "AI Edge Vision Object Classifier",
    domain: "AI",
    ageRange: "10-15 Yrs",
    technology: "OpenCV, Raspberry Pi 4, MobileNet Neural Net",
    skills: ["Computer Vision", "Python", "Dataset Labeling"],
    description: "On-device AI optical system for real-time sorting of color-coded game elements on competition arenas.",
    challenge: "Maintaining high inference rates under variable room ambient light.",
    idea: "Train lightweight convolutional neural network models quantized for TPU acceleration.",
    design: "3D-printed modular camera bracket with ring light LED diffuser.",
    programming: "Python script executing 30 FPS visual tracking with dynamic bounding boxes.",
    testing: "Tested under 300 to 1200 lux lighting shifts across 500 image frames.",
    result: "98.4% detection accuracy achieved at 33 milliseconds per frame.",
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/tech_ai_vision.png",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/tech_ai_vision.png-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/tech_ai_vision.png-wvc-sizes"
    }
  }, {
    id: "proj-4",
    title: "Modular Sensor Hub & CAN-Bus Network",
    domain: "Automation",
    ageRange: "11-16 Yrs",
    technology: "CAN Bus, Ultrasonic Ranging, I2C Multiplexer",
    skills: ["Hardware Bus Protocol", "Embedded C", "Signal Filtering"],
    description: "Distributed telemetry hub allowing instant hot-swapping of up to 16 environmental and distance sensors.",
    challenge: "Preventing signal crosstalk across long ribbon cables in high-EMF environments.",
    idea: "Implement differential CAN transceiver hardware architecture with twisted pair wires.",
    design: "Compact dual-layer PCB with trace shielding and surface-mount components.",
    programming: "Low-level register configurations with hardware interrupt handlers.",
    testing: "Oscilloscope noise analysis during active DC motor PWM switching.",
    result: "Zero packet drop rate over 100,000 continuous transmission frames.",
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/tech_circuit_assembly.png",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/tech_circuit_assembly.png-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/tech_circuit_assembly.png-wvc-sizes"
    }
  }, {
    id: "proj-5",
    title: "Precision Line-Tracing Arena Robot",
    domain: "Coding",
    ageRange: "8-13 Yrs",
    technology: "8-Channel IR Array, Micro:bit v2, PID Logic",
    skills: ["Algorithm Tuning", "State Machines", "Logic Trees"],
    description: "High-speed track navigating robot utilizing dual core motor drivers and continuous reflectance sensing.",
    challenge: "Navigating sharp 90-degree vector intersections without line overshoot.",
    idea: "Dual IR sensor threshold logic integrated with active dynamic motor braking.",
    design: "Low-center-of-mass acrylic chassis with high-grip silicone tires.",
    programming: "Block-to-JavaScript transition code using non-blocking timer loops.",
    testing: "Continuous lap timing across complex multi-branch track layouts.",
    result: "Completed standard 3-meter competition track layout in under 4.2 seconds.",
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/tech_line_follower.png",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/tech_line_follower.png-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/tech_line_follower.png-wvc-sizes"
    }
  }, {
    id: "proj-6",
    title: "Automated Sorting Arena Mat System",
    domain: "Automation",
    ageRange: "9-14 Yrs",
    technology: "Conveyor Belts, Color Sensors, Servo Actuators",
    skills: ["System Integration", "PLC Logic", "Mechanical Linkage"],
    description: "Full arena staging grid simulating smart logistics sorting with optical triggers and pneumatics.",
    challenge: "Synchronizing conveyor speed with multi-gate diverter servo timing.",
    idea: "Optical beam-break trigger sensors initializing queue-based delay timers.",
    design: "Modular extruded aluminum profile frame with quick-release track guides.",
    programming: "Sequential state machine architecture with hardware emergency stop override.",
    testing: "Batch sorting runs of 100 mixed payload cubes.",
    result: "Flawless automated routing of 100 consecutive payload units.",
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/tech_arena_mat.png",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/tech_arena_mat.png-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/tech_arena_mat.png-wvc-sizes"
    }
  }],
  vi: [{
    id: "proj-1",
    title: "Khung Xe Robot Điều Hướng Đa Hướng Omni",
    domain: "Robotics",
    ageRange: "12-16 Tuổi",
    technology: "Bánh Xe Mecanum, Điều Khiển PID, STM32",
    skills: ["Động Học", "C++", "Phản Hồi Vòng Kín"],
    description: "Nền tảng robot hợp kim nhôm chịu lực với chuyển động vectơ độc lập 4 bánh và đo xa mã hóa chính xác.",
    challenge: "Triệt tiêu độ trệch hướng khi di chuyển chéo tốc độ cao trên mặt sa bàn.",
    idea: "Áp dụng thuật toán PID tần số cao đồng bộ với cảm biến con quay hồi chuyển 6 trục IMU.",
    design: "Khung nhôm 6061 gia công CNC với đệm giảm chấn tấm kép.",
    programming: "Lập trình lập lịch thời gian thực bằng C++ với thư viện toán học tùy chỉnh.",
    testing: "Thử nghiệm trên thảm vinyl ma sát thấp để xác minh khả năng quay góc 0 độ.",
    result: "Độ chính xác định vị dưới 1 millimeter qua các lần chạy tự động 5 mét.",
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/tech_rover_chassis.png",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/tech_rover_chassis.png-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/tech_rover_chassis.png-wvc-sizes"
    }
  }, {
    id: "proj-2",
    title: "Mô Hình Cánh Tay Robot Khí Nén 6 Bậc Tự Do",
    domain: "Engineering",
    ageRange: "14-16 Tuổi",
    technology: "Điện Khí Nén, Mạch CAN/SPI, PCB Tùy Chỉnh",
    skills: ["Thiết Kế Mạch CAD", "Logic Khí Nén", "Ma Trận Rơ-Lê"],
    description: "Cánh tay robot khớp nối truyền động bằng van điện từ tỷ lệ và cảm biến góc hiệu ứng Hall từ tính.",
    challenge: "Đạt được sự điều áp mượt mà mà không gây giật chuyển động bất ngờ.",
    idea: "Mạch điều khiển van PWM kết hợp với vòng phản hồi quang học.",
    design: "Khớp nối acrylic cắt laser và polycarbonate gia cường với ổ bi đệm.",
    programming: "Kịch bản MicroPython nhúng với đọc cảm biến đa luồng.",
    testing: "Kiểm tra độ bền 500 chu kỳ gắp và đặt với tải trọng 250g.",
    result: "Độ lặp lại vị trí gắp đạt trong khoảng dung sai ±0.5mm.",
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/tech_robotic_arm.webp",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/tech_robotic_arm.webp-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/tech_robotic_arm.webp-wvc-sizes"
    }
  }, {
    id: "proj-3",
    title: "Hệ Thống Phân Loại Thị Giác Máy Tính AI Edge",
    domain: "AI",
    ageRange: "10-15 Tuổi",
    technology: "OpenCV, Raspberry Pi 4, Mạng Nhận Dạng MobileNet",
    skills: ["Thị Giác Máy Tính", "Python", "Gán Nhãn Dữ Liệu"],
    description: "Hệ thống thị giác AI trên thiết bị để phân loại thời gian thực các vật thể màu sắc trên sa bàn thi đấu.",
    challenge: "Duy trì tốc độ suy luận cao dưới ánh sáng môi trường thay đổi.",
    idea: "Huấn luyện mô hình mạng nơ-ron cuộn nhẹ được lượng thể hóa cho tăng tốc TPU.",
    design: "Giá đỡ camera in 3D mô-đun với vòng đèn LED tán sáng.",
    programming: "Mã Python thực thi theo dõi thị giác 30 FPS với khung định vị động.",
    testing: "Thử nghiệm dưới cường độ sáng 300 đến 1200 lux qua 500 khung hình.",
    result: "Đạt độ chính xác phát hiện 98.4% ở tốc độ 33 miligiây mỗi khung hình.",
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/tech_ai_vision.png",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/tech_ai_vision.png-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/tech_ai_vision.png-wvc-sizes"
    }
  }, {
    id: "proj-4",
    title: "Mạch Trung Tâm Cảm Biến & Mạng Mạch CAN-Bus",
    domain: "Automation",
    ageRange: "11-16 Tuổi",
    technology: "CAN Bus, Định Vị Siêu Âm, I2C Multiplexer",
    skills: ["Giao Thức Mạch Phần Cứng", "Embedded C", "Lọc Tín Hiệu"],
    description: "Mạch đo xa phân tán cho phép cắm nóng tức thì lên đến 16 cảm biến khoảng cách và môi trường.",
    challenge: "Nhiễu tín hiệu trên dây cáp dài trong môi trường từ trường cao.",
    idea: "Triển khai kiến trúc phần cứng CAN vi sai với dây xoắn đôi.",
    design: "Bo mạch PCB hai lớp gọn nhẹ với lớp phủ chống nhiễu.",
    programming: "Cấu hình thanh ghi mức thấp với trình xử lý ngắt phần cứng.",
    testing: "Phân tích nhiễu sóng dao động khi động cơ DC đang bật PWM.",
    result: "Tỷ lệ mất gói tin bằng 0 qua 100,000 khung truyền liên tục.",
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/tech_circuit_assembly.png",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/tech_circuit_assembly.png-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/tech_circuit_assembly.png-wvc-sizes"
    }
  }, {
    id: "proj-5",
    title: "Robot Dò Đường Tốc Độ Cao Sa Bàn Precision",
    domain: "Coding",
    ageRange: "8-13 Tuổi",
    technology: "Mảng IR 8 Kênh, Micro:bit v2, Thuật Toán PID",
    skills: ["Tối Ưu Thuật Toán", "Máy Trạng Thái", "Cây Logic"],
    description: "Robot di chuyển đường đua tốc độ cao sử dụng mạch lái động cơ kép và cảm biến phản xạ liên tục.",
    challenge: "Xử lý các góc rẽ vuông 90 độ mà không bị văng khỏi vạch.",
    idea: "Logic ngưỡng cảm biến IR kép kết hợp với phanh động cơ chủ động.",
    design: "Khung acrylic trọng tâm thấp với lốp cao su silicone bám đường.",
    programming: "Chuyển đổi từ Khối sang C++ / JS sử dụng vòng lặp hẹn giờ không chặn.",
    testing: "Đo thời gian hoàn tất vòng đua trên nhiều sơ đồ đường phức tạp.",
    result: "Hoàn thành đường đua sa bàn 3 mét tiêu chuẩn trong dưới 4.2 giây.",
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/tech_line_follower.png",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/tech_line_follower.png-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/tech_line_follower.png-wvc-sizes"
    }
  }, {
    id: "proj-6",
    title: "Hệ Thống Băng Tải Phân Loại Sa Bàn Tự Động",
    domain: "Automation",
    ageRange: "9-14 Tuổi",
    technology: "Băng Tải Tự Động, Cảm Biến Màu, Động Cơ Servo",
    skills: ["Tích Hợp Hệ Thống", "Logic PLC", "Liên Kết Cơ Khí"],
    description: "Mô hình sa bàn mô phỏng phân loại kho hàng thông minh với cảm biến kích hoạt và xilanh khí nén.",
    challenge: "Đồng bộ tốc độ băng tải với thời gian kích mở cửa ngắt servo.",
    idea: "Cảm biến ngắt chùm tia quang học khởi tạo bộ đếm thời gian trễ hàng chờ.",
    design: "Khung nhôm định hình mô-đun với thanh hướng hướng tháo lắp nhanh.",
    programming: "Kiến trúc máy trạng thái tuần tự với ghi đè dừng khẩn cấp phần cứng.",
    testing: "Chạy phân loại theo lô 100 khối hàng hỗn hợp.",
    result: "Định tuyến tự động chuẩn xác 100 đơn vị hàng hóa liên tiếp.",
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/tech_arena_mat.png",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/tech_arena_mat.png-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/tech_arena_mat.png-wvc-sizes"
    }
  }]
};

// Language Dictionary UI Copy
const UI_TEXT = {
  en: {
    sectionBadge: "TECHNICAL SHOWCASE",
    title: "Engineering Excellence & Robotics Innovation",
    subtitle: "A deep dive into the mechanical chassis, high-speed microcontrollers, embedded sensor networks, and autonomous arenas developed in our labs.",
    tabAll: "All Fields",
    tabRobotics: "Robotics",
    tabCoding: "Coding",
    tabAI: "AI & Vision",
    tabAutomation: "Automation",
    tabEngineering: "Engineering",
    inspectSpec: "Inspect Build Specs",
    viewDetails: "View Details",
    ageLabel: "Target Age",
    techStackLabel: "Technology Stack",
    skillsLabel: "Engineering Concepts",
    challengeTitle: "Engineering Challenge",
    ideaTitle: "Technical Innovation",
    designTitle: "Mechanical Design",
    programmingTitle: "Software Architecture",
    testingTitle: "Testing Methodology",
    resultTitle: "Demonstrated Performance",
    closeModal: "Close Details",
    noProjectsFound: "No technical projects match the selected filter.",
    retryBtn: "Retry"
  },
  vi: {
    sectionBadge: "TRIỂN LÃM KỸ THUẬT",
    title: "Chất Lượng Kỹ Thuật & Đổi Mới Sáng Tạo Robotics",
    subtitle: "Khám phá chi tiết các bộ khung cơ khí, vi điều khiển tốc độ cao, mạng cảm biến nhúng và sa bàn tự động hóa được phát triển tại phòng lab.",
    tabAll: "Tất Cả",
    tabRobotics: "Robotics",
    tabCoding: "Lập Trình",
    tabAI: "AI & Thị Giác",
    tabAutomation: "Tự Động Hóa",
    tabEngineering: "Cơ Khí & Kỹ Thuật",
    inspectSpec: "Xem Thông Số Kỹ Thuật",
    viewDetails: "Chi Tiết Dự Án",
    ageLabel: "Lứa Tuổi",
    techStackLabel: "Công Nghệ Sử Dụng",
    skillsLabel: "Khái Niệm Kỹ Thuật",
    challengeTitle: "Thách Thức Kỹ Thuật",
    ideaTitle: "Giải Pháp Đổi Mới",
    designTitle: "Thiết Kế Cơ Khí",
    programmingTitle: "Kiến Trúc Phần Mềm",
    testingTitle: "Phương Pháp Thử Nghiệm",
    resultTitle: "Hiệu Suất Thực Tế",
    closeModal: "Đóng Chi Tiết",
    noProjectsFound: "Không tìm thấy dự án kỹ thuật nào phù hợp.",
    retryBtn: "Thử lại"
  }
};

// Component rendering single project card
function TechnicalCard({
  project,
  lang
}: {
  project: TechnicalProject;
  lang: "en" | "vi";
}) {
  const t = UI_TEXT[lang];
  return <Card className="group relative flex flex-col justify-between overflow-hidden border border-border bg-card shadow-xs transition-all duration-300 hover:-translate-y-1 hover:border-primary/40 hover:shadow-lg">
      <div>
        {/* Technical Image Container */}
        <div className="relative aspect-16/10 w-full overflow-hidden bg-muted">
          <img src={project.image.src} data-wvc-srcset={project.image.srcset} data-wvc-sizes={project.image.sizes} alt={project.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

          {/* Top Badges */}
          <div className="absolute top-3 left-3 right-3 flex items-center justify-between gap-2">
            <Badge className="bg-primary/90 text-primary-foreground font-medium backdrop-blur-xs border-none shadow-xs">
              {project.domain}
            </Badge>
            <Badge variant="outline" className="bg-background/80 text-foreground text-xs backdrop-blur-xs border-border">
              {project.ageRange}
            </Badge>
          </div>

          {/* Technology Label overlay on bottom of image */}
          <div className="absolute bottom-2.5 left-3 right-3 flex items-center gap-1.5 text-xs text-white/90">
            <Cpu className="size-3.5 text-accent shrink-0" />
            <span className="truncate font-mono text-[11px] font-medium tracking-tight">
              {project.technology}
            </span>
          </div>
        </div>

        {/* Card Header & Description */}
        <CardHeader className="p-5 pb-2">
          <CardTitle className="line-clamp-2 text-lg font-bold leading-snug group-hover:text-primary transition-colors">
            {project.title}
          </CardTitle>
        </CardHeader>

        <CardContent className="p-5 pt-0 space-y-4">
          <p className="line-clamp-3 text-sm text-muted-foreground leading-relaxed">
            {project.description}
          </p>

          {/* Skills / Concept Chips */}
          {project.skills && project.skills.length > 0 && <div className="flex flex-wrap gap-1.5 pt-1">
              {project.skills.slice(0, 3).map((skill, idx) => <span key={idx} className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground border border-border/50">
                  <Microchip className="size-3 text-secondary shrink-0" />
                  {skill}
                </span>)}
            </div>}
        </CardContent>
      </div>

      {/* Card Actions */}
      <CardFooter className="p-5 pt-0 flex items-center justify-between gap-2 border-t border-border/40 mt-4">
        <Dialog>
          <DialogTrigger asChild>
            <Button variant="outline" size="sm" className="w-full justify-center gap-2 border-border hover:border-primary hover:bg-primary/5 text-xs font-semibold">
              <Wrench className="size-3.5 text-primary" />
              {t.inspectSpec}
            </Button>
          </DialogTrigger>

          {/* Detailed Technical Spec Modal */}
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto p-6">
            <DialogHeader className="border-b border-border pb-4">
              <div className="flex items-center gap-2 mb-1">
                <Badge className="bg-primary text-primary-foreground">{project.domain}</Badge>
                <span className="text-xs text-muted-foreground">{t.ageLabel}: {project.ageRange}</span>
              </div>
              <DialogTitle className="text-xl font-bold leading-tight text-foreground">
                {project.title}
              </DialogTitle>
              <DialogDescription className="text-sm text-muted-foreground font-mono pt-1">
                {t.techStackLabel}: {project.technology}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-5 pt-4 text-sm">
              {/* Overview */}
              <div>
                <h4 className="font-semibold text-foreground mb-1.5 flex items-center gap-2">
                  <Layers className="size-4 text-primary" />
                  {lang === "en" ? "System Architecture Summary" : "Tóm Tắt Kiến Trúc Hệ Thống"}
                </h4>
                <p className="text-muted-foreground leading-relaxed">{project.description}</p>
              </div>

              {/* Challenge & Idea */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {project.challenge && <div className="rounded-lg bg-muted/60 p-3.5 border border-border/60">
                    <h5 className="font-semibold text-xs text-foreground uppercase tracking-wider mb-1 text-destructive/90">
                      {t.challengeTitle}
                    </h5>
                    <p className="text-xs text-muted-foreground leading-relaxed">{project.challenge}</p>
                  </div>}
                {project.idea && <div className="rounded-lg bg-muted/60 p-3.5 border border-border/60">
                    <h5 className="font-semibold text-xs text-foreground uppercase tracking-wider mb-1 text-secondary font-bold">
                      {t.ideaTitle}
                    </h5>
                    <p className="text-xs text-muted-foreground leading-relaxed">{project.idea}</p>
                  </div>}
              </div>

              {/* Design & Programming */}
              {(project.design || project.programming) && <div className="space-y-3 rounded-lg border border-border p-4 bg-card">
                  {project.design && <div>
                      <h5 className="font-semibold text-xs text-foreground uppercase tracking-wide mb-1 flex items-center gap-1.5">
                        <Wrench className="size-3.5 text-accent" />
                        {t.designTitle}
                      </h5>
                      <p className="text-xs text-muted-foreground">{project.design}</p>
                    </div>}
                  {project.programming && <div className="pt-2 border-t border-border/50">
                      <h5 className="font-semibold text-xs text-foreground uppercase tracking-wide mb-1 flex items-center gap-1.5">
                        <Code className="size-3.5 text-primary" />
                        {t.programmingTitle}
                      </h5>
                      <p className="text-xs font-mono bg-muted p-2 rounded text-muted-foreground">{project.programming}</p>
                    </div>}
                </div>}

              {/* Testing & Verified Result */}
              {(project.testing || project.result) && <div className="rounded-lg bg-primary/5 border border-primary/20 p-4 space-y-2">
                  {project.testing && <div>
                      <span className="font-semibold text-xs text-foreground block mb-0.5">{t.testingTitle}:</span>
                      <p className="text-xs text-muted-foreground">{project.testing}</p>
                    </div>}
                  {project.result && <div className="flex items-start gap-2 pt-1">
                      <CheckCircle2 className="size-4 text-secondary shrink-0 mt-0.5" />
                      <div>
                        <span className="font-semibold text-xs text-foreground block">{t.resultTitle}:</span>
                        <p className="text-xs font-medium text-foreground">{project.result}</p>
                      </div>
                    </div>}
                </div>}
            </div>

            {project.link && <div className="pt-4 border-t border-border flex justify-end">
                <Button asChild size="sm" className="gap-2">
                  <Link to={project.link}>
                    {t.viewDetails} <ExternalLink className="size-3.5" />
                  </Link>
                </Button>
              </div>}
          </DialogContent>
        </Dialog>

        {project.link && <Button asChild variant="ghost" size="icon" className="size-8 shrink-0">
            <Link to={project.link} aria-label={project.title}>
              <ChevronRight className="size-4" />
            </Link>
          </Button>}
      </CardFooter>
    </Card>;
}

// Inner Consumer component inside PostsProvider
function TechnicalShowcaseConsumer({
  lang
}: {
  lang: "en" | "vi";
}) {
  const t = UI_TEXT[lang];
  const {
    posts,
    loading,
    error,
    refetch
  } = usePosts();
  const [selectedDomain, setSelectedDomain] = useState<string>("all");

  // Map WP Custom Post Type `project` items if available
  const wpProjects: TechnicalProject[] = useMemo(() => {
    if (!posts || posts.length === 0) return [];
    return posts.map(post => {
      const meta = post.customFields || {};
      const imgUrl = meta.image || post.featuredImage || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/tech_rover_chassis.png";
      return {
        id: post.id || String(Math.random()),
        title: meta.project_name || post.title || "Technical Build",
        domain: meta.field_domain || "Robotics",
        ageRange: meta.age_range || "8-16 Yrs",
        technology: meta.technology || meta.skills || "Microcontrollers & Sensors",
        skills: meta.skills ? String(meta.skills).split(",").map(s => s.trim()) : ["Engineering", "STEM"],
        description: meta.short_description || post.excerpt || "High-precision engineering build designed for technical performance.",
        challenge: meta.challenge || "",
        idea: meta.idea || "",
        design: meta.design || "",
        programming: meta.programming || "",
        testing: meta.testing || "",
        result: meta.result || meta.skills_developed || "",
        image: {
          src: imgUrl,
          srcset: "",
          sizes: ""
        },
        link: post.link
      };
    });
  }, [posts]);

  // Combined project pool (WP Projects or Fallback)
  const allProjects = useMemo(() => {
    if (wpProjects.length > 0) {
      return wpProjects;
    }
    return FALLBACK_PROJECTS[lang];
  }, [wpProjects, lang]);

  // Filter by Domain
  const filteredProjects = useMemo(() => {
    if (selectedDomain === "all") return allProjects;
    return allProjects.filter(p => {
      const domainLower = p.domain.toLowerCase();
      const selLower = selectedDomain.toLowerCase();
      if (selLower === "ai") return domainLower.includes("ai") || domainLower.includes("vision");
      return domainLower.includes(selLower);
    });
  }, [allProjects, selectedDomain]);
  if (loading) {
    return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[...Array(6)].map((_, i) => <div key={i} className="h-96 rounded-xl bg-muted/40 animate-pulse border border-border" />)}
      </div>;
  }
  if (error) {
    return <div className="text-center py-12 rounded-xl border border-border bg-card p-6 max-w-md mx-auto">
        <p className="text-sm text-muted-foreground mb-4">{error}</p>
        <Button onClick={refetch} variant="outline" size="sm">
          {t.retryBtn}
        </Button>
      </div>;
  }
  return <div className="space-y-8">
      {/* Category Tabs */}
      <div className="flex justify-center">
        <Tabs defaultValue="all" value={selectedDomain} onValueChange={setSelectedDomain} className="w-full max-w-3xl">
          <TabsList className="grid w-full grid-cols-3 sm:grid-cols-6 h-auto p-1 bg-muted/80 rounded-xl gap-1">
            <TabsTrigger value="all" className="text-xs font-semibold py-2">
              {t.tabAll}
            </TabsTrigger>
            <TabsTrigger value="robotics" className="text-xs font-semibold py-2">
              {t.tabRobotics}
            </TabsTrigger>
            <TabsTrigger value="coding" className="text-xs font-semibold py-2">
              {t.tabCoding}
            </TabsTrigger>
            <TabsTrigger value="ai" className="text-xs font-semibold py-2">
              {t.tabAI}
            </TabsTrigger>
            <TabsTrigger value="automation" className="text-xs font-semibold py-2">
              {t.tabAutomation}
            </TabsTrigger>
            <TabsTrigger value="engineering" className="text-xs font-semibold py-2">
              {t.tabEngineering}
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Grid of Technical Projects */}
      {filteredProjects.length === 0 ? <div className="text-center py-16 bg-card border border-border rounded-xl">
          <Bot className="size-10 text-muted-foreground mx-auto mb-3 opacity-50" />
          <p className="text-sm text-muted-foreground">{t.noProjectsFound}</p>
        </div> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProjects.map((project, i) => <div key={project.id} data-index={i}>
              <TechnicalCard project={project} lang={lang} />
            </div>)}
        </div>}
    </div>;
}

// Main Root Exported Component
export default function TechnicalShowcase() {
  const [lang, setLang] = useState<"en" | "vi">("en");

  // Bilingual Listener
  useEffect(() => {
    const getLang = (): "en" | "vi" => {
      if (typeof window === "undefined") return "en";
      const saved = localStorage.getItem("roboto_lang");
      return saved === "vi" ? "vi" : "en";
    };
    setLang(getLang());
    const handleLangChange = () => {
      setLang(getLang());
    };
    window.addEventListener("roboto-lang-change", handleLangChange);
    window.addEventListener("storage", handleLangChange);
    return () => {
      window.removeEventListener("roboto-lang-change", handleLangChange);
      window.removeEventListener("storage", handleLangChange);
    };
  }, []);
  const t = UI_TEXT[lang];

  // Configure WP_Query for project CPT
  const wpQuery = useMemo(() => {
    return new WP_Query({
      post_type: "project",
      per_page: 12,
      orderby: "date",
      order: "DESC"
    });
  }, []);
  return <section id="technicalshowcase" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 overflow-hidden border-b border-border">
      {/* Background Microkit Grid Overlay */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.03] bg-[radial-gradient(var(--border)_1px,transparent_1px)] [background-size:24px_24px]" aria-hidden="true" />

      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12 space-y-4">
          <Badge variant="outline" className="border-primary/30 text-primary bg-primary/5 px-3 py-1 font-mono text-xs uppercase tracking-wider inline-flex items-center gap-1.5">
            <Sparkles className="size-3 text-primary" />
            {t.sectionBadge}
          </Badge>

          <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-foreground font-serif">
            {t.title}
          </h2>

          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
            {t.subtitle}
          </p>
        </div>

        {/* Dynamic Posts Consumer wrapped in PostsProvider */}
        <PostsProvider wp_query={wpQuery}>
          <TechnicalShowcaseConsumer lang={lang} />
        </PostsProvider>
      </div>
    </section>;
}